import { Component } from "@theme/component";

/**
 * "Deel jouw favoriet" on the product page.
 *
 * Touch devices with the Web Share API get the native share sheet; other
 * browsers get a small menu with the social share links + copy link.
 *
 * Refs:
 * - menu: the share menu (hidden by default)
 * - copyButton / copyLabel: copy-link button and its label
 */
class ProductShareButton extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    document.addEventListener(
      "click",
      (event) => {
        if (!this.contains(/** @type {Node} */ (event.target))) this.close();
      },
      { signal }
    );
    document.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape") this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#abortController.abort();
  }

  get #shareData() {
    return {
      title: this.dataset.shareTitle || document.title,
      url: this.dataset.shareUrl || window.location.href,
    };
  }

  get #trigger() {
    return this.querySelector(".product-share-button__trigger");
  }

  get #canNativeShare() {
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    return isTouch && typeof navigator.share === "function";
  }

  async toggle() {
    if (this.#canNativeShare) {
      try {
        await navigator.share(this.#shareData);
      } catch {
        // User dismissed the sheet or the browser refused: fall back to the menu.
        if (!this.refs.menu.hidden) return;
        this.open();
      }
      return;
    }

    if (this.refs.menu.hidden) {
      this.open();
    } else {
      this.close();
    }
  }

  open() {
    this.refs.menu.hidden = false;
    this.#trigger?.setAttribute("aria-expanded", "true");
  }

  close() {
    if (this.refs.menu.hidden) return;
    this.refs.menu.hidden = true;
    this.#trigger?.setAttribute("aria-expanded", "false");
    this.#resetCopyLabel();
  }

  async copyLink() {
    const { copyLabel } = this.refs;
    try {
      await navigator.clipboard.writeText(this.#shareData.url);
      if (copyLabel) {
        copyLabel.dataset.original ??= copyLabel.textContent ?? "";
        copyLabel.textContent = this.dataset.copiedLabel || copyLabel.textContent;
      }
      setTimeout(() => this.#resetCopyLabel(), 2000);
    } catch {
      window.prompt("", this.#shareData.url);
    }
  }

  #resetCopyLabel() {
    const { copyLabel } = this.refs;
    if (copyLabel?.dataset.original) copyLabel.textContent = copyLabel.dataset.original;
  }
}

if (!customElements.get("product-share-button")) {
  customElements.define("product-share-button", ProductShareButton);
}
